import React, { useState, useEffect } from 'react';
import { getAllUsers, updateUserSubscription, deleteUser, createUser } from '../../services/admin';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newUser, setNewUser] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    levelId: 'grade7',
    role: 'student',
    subscriptionStatus: 'unpaid',
    password: ''
  });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersData = await getAllUsers(100); // Get more users for admin
        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.lastName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesLevel = !filterLevel || user.levelId === filterLevel;
    const matchesStatus = !filterStatus || user.subscription?.status === filterStatus;
    
    return matchesSearch && matchesLevel && matchesStatus;
  });

  const handleUpdateSubscription = async (userId, subscriptionData) => {
    try {
      await updateUserSubscription(userId, subscriptionData);
      
      // Update local state
      setUsers(users.map(user => 
        user.id === userId 
          ? { ...user, subscription: subscriptionData }
          : user
      ));
      
      setShowEditModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error updating subscription:', error);
      alert('Failed to update subscription. Please try again.');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    try {
      await deleteUser(userId);
      setUsers(users.filter(user => user.id !== userId));
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user. Please try again.');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.firstName || !newUser.lastName || !newUser.email || !newUser.password) {
      alert('Please fill in all required fields (First Name, Last Name, Email, Password).');
      return;
    }

    if (!newUser.email.includes('@')) {
      alert('Please enter a valid email address.');
      return;
    }

    if (newUser.password.length < 6) {
      alert('Password must be at least 6 characters long.');
      return;
    }

    try {
      const createdUser = await createUser(newUser);
      setUsers([createdUser, ...users]);
      setShowAddModal(false);
      setNewUser({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        levelId: 'grade7',
        role: 'student',
        subscriptionStatus: 'unpaid',
        password: ''
      });
      alert('User created successfully! Password has been set for the user.');
      
      // Refresh users list to get updated data
      const usersData = await getAllUsers(100);
      setUsers(usersData);
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Failed to create user. Please try again.');
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      active: 'green',
      payment_pending: 'yellow',
      unpaid: 'red',
      expired: 'gray'
    };
    
    return (
      <span className={`status-badge ${statusColors[status] || 'gray'}`}>
        {status || 'unpaid'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="user-management">
        <div className="loading-container">
          <div className="loading-spinner">Loading users...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="page-header">
        <div>
          <h1>User Management</h1>
          <p>Manage user accounts and subscriptions</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary"
        >
          Add New User
        </button>
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <select
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value)}
            className="filter-select"
          >
            <option value="">All Levels</option>
            <option value="grade7">Grade 7</option>
            <option value="form2">Form 2</option>
            <option value="grade12">Grade 12</option>
          </select>
        </div>

        <div className="filter-group">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="payment_pending">Payment Pending</option>
            <option value="unpaid">Unpaid</option>
            <option value="expired">Expired</option>
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Email</th>
              <th>Level</th>
              <th>Status</th>
              <th>Joined</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id}>
                <td>
                  <div className="user-info">
                    <div className="user-avatar">
                      {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                    </div>
                    <div>
                      <div className="user-name">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="user-role">
                        {user.role || 'student'}
                      </div>
                    </div>
                  </div>
                </td>
                <td>{user.email}</td>
                <td>
                  <span className="level-badge">
                    {user.levelId?.replace('grade', 'Grade ').replace('form', 'Form ')}
                  </span>
                </td>
                <td>{getStatusBadge(user.subscription?.status)}</td>
                <td>{formatDate(user.createdAt)}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      onClick={() => openEditModal(user)}
                      className="btn btn-sm btn-secondary"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      className="btn btn-sm btn-danger"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredUsers.length === 0 && (
          <div className="no-results">
            <p>No users found matching your criteria.</p>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {showEditModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Edit User Subscription</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="modal-close"
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              <div className="user-details">
                <h4>{selectedUser.firstName} {selectedUser.lastName}</h4>
                <p>{selectedUser.email}</p>
                <p>Level: {selectedUser.levelId?.replace('grade', 'Grade ').replace('form', 'Form ')}</p>
              </div>

              <div className="form-group">
                <label>Subscription Status</label>
                <select
                  value={selectedUser.subscription?.status || 'unpaid'}
                  onChange={(e) => setSelectedUser({
                    ...selectedUser,
                    subscription: {
                      ...selectedUser.subscription,
                      status: e.target.value
                    }
                  })}
                  className="form-control"
                >
                  <option value="unpaid">Unpaid</option>
                  <option value="payment_pending">Payment Pending</option>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                </select>
              </div>

              {selectedUser.subscription?.status === 'active' && (
                <div className="form-group">
                  <label>Expiry Date</label>
                  <input
                    type="date"
                    value={selectedUser.subscription?.expiresAt ? 
                      new Date(selectedUser.subscription.expiresAt.toDate()).toISOString().split('T')[0] : 
                      ''
                    }
                    onChange={(e) => setSelectedUser({
                      ...selectedUser,
                      subscription: {
                        ...selectedUser.subscription,
                        expiresAt: new Date(e.target.value)
                      }
                    })}
                    className="form-control"
                  />
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button
                onClick={() => setShowEditModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleUpdateSubscription(selectedUser.id, selectedUser.subscription)}
                className="btn btn-primary"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Add New User</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="modal-close"
              >
                ×
              </button>
            </div>

            <div className="modal-content">
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={newUser.firstName}
                    onChange={(e) => setNewUser({...newUser, firstName: e.target.value})}
                    className="form-control"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={newUser.lastName}
                    onChange={(e) => setNewUser({...newUser, lastName: e.target.value})}
                    className="form-control"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                    className="form-control"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Password *</label>
                  <input
                    type="password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                    className="form-control"
                    placeholder="Enter initial password"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={newUser.phone}
                    onChange={(e) => setNewUser({...newUser, phone: e.target.value})}
                    className="form-control"
                  />
                </div>

                <div className="form-group">
                  <label>Level</label>
                  <select
                    value={newUser.levelId}
                    onChange={(e) => setNewUser({...newUser, levelId: e.target.value})}
                    className="form-control"
                  >
                    <option value="grade7">Grade 7</option>
                    <option value="grade12">Grade 12</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Subscription Status</label>
                  <select
                    value={newUser.subscriptionStatus}
                    onChange={(e) => setNewUser({...newUser, subscriptionStatus: e.target.value})}
                    className="form-control"
                  >
                    <option value="unpaid">Unpaid</option>
                    <option value="payment_pending">Payment Pending</option>
                    <option value="active">Active</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button
                onClick={() => setShowAddModal(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                className="btn btn-primary"
              >
                Create User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
